// src/components/vault/documents/DataValidation.tsx
//
// Virtual "Validacion de Informacion" step.
// Combines OCR data from the ID and Proof of Address requirements,
// lets the user confirm or edit, then persists `confirmed_data` on the
// ID requirement via the workflow-step endpoint.

import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { useAppSelector } from "../../../redux/hooks";
import { useUpdateWorkflowStep } from "../../../hooks/vault/mutations/useUpdateWorkflowStep";
import { useVaultFolder } from "../../../hooks/vault/queries/useVaultFolder";
import { useBdcStatus } from "../../../hooks/vault/queries/useBdcStatus";
import { useSingleFlight } from "../../../hooks/useSingleFlight";
import {
  isBdcAuthorizationRequirement,
  isIdentityRequirement,
  isProofOfAddressRequirement,
  hasValidatedPersonForm,
  canCaptureBdcIdentity,
  getBdcSicSource,
  isLegacyBdcCaptureRecoveryEligible,
  PERSON_FORM_SLUGS,
} from "../utils/requirementGrouping";
import { IDDataConfirmationForm } from "./id-validation/IDDataConfirmationForm";
import { IDDataEditForm } from "./id-validation/IDDataEditForm";
import {
  DEFAULT_NIP_DESTINATION_DIAL_CODE,
  NIP_DESTINATION_DIAL_CODE_FIELD,
  NIP_DESTINATION_REVIEWED_FIELD,
  hasReviewedNipDestination,
  hasValidIDData,
  isBdcNaturalPersonSubfolder,
  isValidNipDestinationDialCode,
  normalizeNipPhoneNumber,
  isDataValidationSubmittedWorkflowStep,
  normalizeIDFieldErrors,
  validateIDData,
} from "./id-validation/idDataValidation";
import type {
  ExtractedINEData,
  ExtractedAddress,
  NipDestinationDialCode,
} from "./id-validation/types";
import {
  isDeterministicBdcError,
  mapBdcAxiosError,
} from "../../../exceptions/creditReportExceptions";
import {
  summarizeCombinedVerificationData,
  verificationDebugLog,
} from "../../../utils/verificationDebug";
import {
  isBdcActorMismatch,
  decodeVaultJwtActor,
  isNativeBdcSupported,
} from "../../../utils/vault/bdcActor";
import type {
  Folder,
  Requirement,
  Subfolder,
} from "../../../redux/interfaces/folder.types";
import { ProofOfAddressProcessingLoader } from "./id-validation/ProofOfAddressProcessingLoader";
import { Status } from "../../../redux/interfaces/folder.types";

// ---------------------------------------------------------------------------
// Helpers to extract structured data from verification_data
// ---------------------------------------------------------------------------

const EMPTY_ADDRESS: ExtractedAddress = {
  calle: "",
  numeroExterior: "",
  numeroInterior: "",
  colonia: "",
  codigoPostal: "",
  municipio: "",
  estado: "",
};

const EMPTY_INE_DATA: ExtractedINEData = {
  nombre: "",
  segundoNombre: "",
  apellidoPaterno: "",
  apellidoMaterno: "",
  direccion: EMPTY_ADDRESS,
  curp: "",
  rfc: "",
  claveElector: "",
  fechaNacimiento: "",
  seccionElectoral: "",
  vigencia: "",
  genero: "",
  email: "",
  phone_number: "",
  dial_code: DEFAULT_NIP_DESTINATION_DIAL_CODE,
};

type DataValidationWorkflowStep = "data_edit" | "completed";

interface ConfirmedDataPayloadOptions {
  readonly includeContact?: boolean;
  readonly markNipDestinationReviewed?: boolean;
  readonly nipDestinationDialCode?: NipDestinationDialCode;
}

function splitStreetNumber(addressLine: string): {
  calle: string;
  numeroExterior: string;
} {
  const trimmed = addressLine.trim();
  const match = trimmed.match(/^(.+?)\s+(\d+[\w\-/]*)$/);
  if (!match) return { calle: trimmed, numeroExterior: "" };

  return {
    calle: match[1].trim(),
    numeroExterior: match[2].trim(),
  };
}

type UnknownRecord = Record<string, unknown>;
type MetadataContactKey = "tenant" | "guarantor" | "landlord" | "advisor";

interface ContactData {
  readonly email: string;
  readonly phone_number: string;
  readonly dial_code?: NipDestinationDialCode;
}

function withoutBdcContactForDebug(
  data: ExtractedINEData,
): Record<string, unknown> {
  const payload: Record<string, unknown> = { ...data };
  delete payload.email;
  delete payload.phone_number;
  delete payload.dial_code;
  return payload;
}

function isNonEmptyRecord(value: unknown): value is UnknownRecord {
  return (
    value !== null &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length > 0
  );
}

function getWorkflowFieldErrors(error: unknown): Record<string, string> {
  if (!isNonEmptyRecord(error) || error.target_slug !== "official-id") return {};
  return normalizeIDFieldErrors(error.field_errors);
}

function toText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
}

function getText(
  record: UnknownRecord | null | undefined,
  ...keys: readonly string[]
): string {
  if (!record) return "";
  for (const key of keys) {
    const value = toText(record[key]);
    if (value) return value;
  }
  return "";
}

function toDialCode(value: unknown): NipDestinationDialCode | null {
  const text = toText(value).trim();
  if (isValidNipDestinationDialCode(text)) return text;
  if (text === "52") return "+52";
  if (text === "1") return "+1";
  return null;
}

function getRecord(
  record: UnknownRecord | null | undefined,
  ...keys: readonly string[]
): UnknownRecord | null {
  if (!record) return null;
  for (const key of keys) {
    const value = record[key];
    if (isNonEmptyRecord(value)) return value;
  }
  return null;
}

function extractContactData(
  record: UnknownRecord | null | undefined,
): ContactData {
  const dialCode = toDialCode(getText(record, "dial_code", "dialCode", "lada"));

  return {
    email: getText(record, "email", "correo").trim().toLowerCase(),
    phone_number: normalizeNipPhoneNumber(
      getText(record, "phone_number", "phoneNumber", "telefono", "phone"),
    ),
    ...(dialCode ? { dial_code: dialCode } : {}),
  };
}

function getMetadataContactKey(
  subfolderSlug: string | null | undefined,
): MetadataContactKey | null {
  if (!subfolderSlug) return null;
  if (subfolderSlug.includes("tenant")) return "tenant";
  if (subfolderSlug.includes("guarantor")) return "guarantor";
  if (subfolderSlug.includes("landlord") || subfolderSlug.includes("owner")) {
    return "landlord";
  }
  if (subfolderSlug.includes("advisor")) return "advisor";
  return null;
}

function getActorContactData(
  metadata: unknown,
  subfolderSlug: string | null | undefined,
): ContactData | null {
  const metadataRecord = isNonEmptyRecord(metadata) ? metadata : null;
  const contactKey = getMetadataContactKey(subfolderSlug);
  const contactRecord = contactKey
    ? getRecord(metadataRecord, contactKey)
    : null;

  return contactRecord ? extractContactData(contactRecord) : null;
}

function getVerificationContactData(
  verificationData: unknown,
): ContactData | null {
  const verification = isNonEmptyRecord(verificationData)
    ? verificationData
    : null;
  if (!verification) return null;

  const extracted = isNonEmptyRecord(verification.confirmed_data)
    ? verification.confirmed_data
    : isNonEmptyRecord(verification.extracted_data)
      ? verification.extracted_data
      : null;

  return extracted ? extractContactData(extracted) : null;
}

function getParticipantContactData(
  subfolder: Subfolder | null,
  verificationData: unknown,
): ContactData {
  const confirmed = getVerificationContactData(verificationData);
  const personForm = subfolder?.requirements.find((requirement) =>
    PERSON_FORM_SLUGS.includes(requirement.slug),
  );
  const form = extractContactData(
    Object.fromEntries(
      (personForm?.form_values ?? []).map(({ key, value }) => [key, value]),
    ),
  );
  const metadata = isNonEmptyRecord(subfolder?.metadata)
    ? subfolder.metadata
    : null;
  const ownMetadata = extractContactData(metadata);
  return {
    email: confirmed?.email || form.email || ownMetadata.email,
    phone_number:
      confirmed?.phone_number || form.phone_number || ownMetadata.phone_number,
    dial_code: confirmed?.dial_code || form.dial_code || ownMetadata.dial_code,
  };
}

function normalizeStructuredAddress(address: unknown): ExtractedAddress {
  const source = isNonEmptyRecord(address) ? address : null;

  return {
    calle: getText(source, "calle"),
    numeroExterior: getText(source, "numeroExterior", "numero_exterior"),
    numeroInterior: getText(source, "numeroInterior", "numero_interior"),
    colonia: getText(source, "colonia"),
    codigoPostal: getText(source, "codigoPostal", "codigo_postal"),
    municipio: getText(source, "municipio"),
    estado: getText(source, "estado"),
  };
}

function applyContactDefaults(
  data: ExtractedINEData,
  actorContact: ContactData | null,
  dialCode: NipDestinationDialCode,
): ExtractedINEData {
  return {
    ...data,
    email: actorContact?.email || data.email.trim() || "",
    phone_number:
      actorContact?.phone_number ||
      normalizeNipPhoneNumber(data.phone_number) ||
      "",
    dial_code: dialCode,
  };
}

function fillEmptyTextFields<T extends object>(primary: T, fallback: T): T {
  return Object.fromEntries(
    Object.entries(primary).map(([key, value]) => [
      key,
      typeof value === "string" && !value.trim()
        ? fallback[key as keyof T]
        : value,
    ]),
  ) as T;
}

/** Prefill only; the actor must confirm and the server checks approved values. */
function extractApprovedPersonForm(form: Requirement): ExtractedINEData {
  const values = Object.fromEntries(
    (form.form_values ?? []).map(({ key, value }) => [key, value]),
  );
  const gender = getText(values, "gender").trim().toUpperCase();
  return extractPersonalDataFromID({
    extracted_data: {
      nombre: getText(values, "first_name"),
      segundoNombre: getText(values, "second_name"),
      apellidoPaterno: getText(values, "first_last_name"),
      apellidoMaterno: getText(values, "second_last_name"),
      curp: getText(values, "curp"),
      fechaNacimiento: getText(values, "birth_date"),
      genero: ["H", "MASCULINO"].includes(gender)
        ? "Masculino"
        : ["M", "FEMENINO"].includes(gender)
          ? "Femenino"
          : getText(values, "gender"),
      rfc: getText(values, "rfc"),
      direccion: {
        calle: getText(values, "street"),
        numeroExterior: getText(values, "exterior_number"),
        numeroInterior: getText(values, "interior_number"),
        colonia: getText(values, "neighborhood"),
        codigoPostal: getText(values, "postal_code"),
        municipio: getText(values, "city"),
        estado: getText(values, "state"),
      },
    },
  });
}

function buildConfirmedDataPayload(
  data: ExtractedINEData,
  options: ConfirmedDataPayloadOptions = {},
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    nombre: data.nombre,
    segundoNombre: data.segundoNombre,
    apellidoPaterno: data.apellidoPaterno,
    apellidoMaterno: data.apellidoMaterno,
    direccion: {
      ...data.direccion,
      codigoPostal: String(data.direccion.codigoPostal ?? "").trim(),
    },
    curp: data.curp,
    rfc: data.rfc,
    claveElector: data.claveElector,
    fechaNacimiento: data.fechaNacimiento,
    seccionElectoral: data.seccionElectoral,
    vigencia: data.vigencia,
    genero: data.genero,
  };

  if (options.includeContact) {
    payload.email = data.email;
    payload.phone_number = data.phone_number;
    payload.dial_code = options.nipDestinationDialCode ?? data.dial_code;
  }

  if (options.markNipDestinationReviewed) {
    payload[NIP_DESTINATION_REVIEWED_FIELD] = true;
    payload[NIP_DESTINATION_DIAL_CODE_FIELD] =
      options.nipDestinationDialCode ?? DEFAULT_NIP_DESTINATION_DIAL_CODE;
  }

  return payload;
}

function normalizeConfirmedDataForSave(
  data: ExtractedINEData,
): ExtractedINEData {
  const codigoPostal = String(data.direccion.codigoPostal ?? "").trim();
  if (codigoPostal === data.direccion.codigoPostal) return data;
  return {
    ...data,
    direccion: { ...data.direccion, codigoPostal },
  };
}

function normalizeIdentityOcrAddress(ocr: unknown): ExtractedAddress {
  const source = isNonEmptyRecord(ocr) ? ocr : null;
  const address = getRecord(source, "address", "address_details");

  return {
    calle: getText(address, "calle", "street"),
    numeroExterior: getText(
      address,
      "numeroExterior",
      "numero_exterior",
      "exterior_number",
      "external_number",
      "number",
    ),
    numeroInterior: getText(
      address,
      "numeroInterior",
      "numero_interior",
      "interior_number",
      "internal_number",
    ),
    colonia: getText(address, "colonia", "neighborhood", "colony"),
    codigoPostal: getText(
      address,
      "codigoPostal",
      "codigo_postal",
      "zip",
      "zip_code",
    ),
    municipio: getText(
      address,
      "municipio",
      "municipality",
      "municipality_delegation",
    ),
    estado: getText(address, "estado", "state"),
  };
}

/**
 * Pull personal information from the ID requirement's verification_data.
 * Precedence: confirmed_data (user-edited) > extracted_data (already in the
 * app's format) > legacy raw-ocr parsing.
 */
function extractPersonalDataFromID(
  verificationData: unknown,
): ExtractedINEData {
  const verification = isNonEmptyRecord(verificationData)
    ? verificationData
    : null;
  if (!verification) return EMPTY_INE_DATA;

  // Structured shape: confirmed_data/extracted_data are already
  // ExtractedINEData-compatible (camelCase or snake_case)
  const extracted = isNonEmptyRecord(verification.confirmed_data)
    ? verification.confirmed_data
    : isNonEmptyRecord(verification.extracted_data)
      ? verification.extracted_data
      : null;
  if (extracted) {
    const extractedAddress = getRecord(extracted, "direccion");
    const contact = extractContactData(extracted);
    return {
      nombre: getText(extracted, "nombre"),
      segundoNombre: getText(extracted, "segundoNombre", "segundo_nombre"),
      apellidoPaterno: getText(
        extracted,
        "apellidoPaterno",
        "apellido_paterno",
      ),
      apellidoMaterno: getText(
        extracted,
        "apellidoMaterno",
        "apellido_materno",
      ),
      direccion: extractedAddress
        ? normalizeStructuredAddress(extractedAddress)
        : EMPTY_ADDRESS,
      curp: getText(extracted, "curp"),
      rfc: getText(extracted, "rfc"),
      claveElector: getText(extracted, "claveElector", "clave_elector"),
      fechaNacimiento: getText(
        extracted,
        "fechaNacimiento",
        "fecha_nacimiento",
      ),
      seccionElectoral: getText(
        extracted,
        "seccionElectoral",
        "seccion_electoral",
      ),
      vigencia: getText(extracted, "vigencia"),
      genero: getText(extracted, "genero"),
      email: contact.email,
      phone_number: contact.phone_number,
      dial_code: contact.dial_code ?? DEFAULT_NIP_DESTINATION_DIAL_CODE,
    };
  }

  // Legacy fallback: old records persisted before extracted_data was stored
  // alongside the raw OCR response. Supports both provider contracts.
  const rawOcr = getRecord(verification, "ocr");
  const ocr = getRecord(rawOcr, "personal_information", "personal_info");
  if (ocr) {
    const name = getRecord(ocr, "name");
    const docData =
      getRecord(rawOcr, "document_identification_data") ??
      getRecord(rawOcr, "electoral_data") ??
      getRecord(rawOcr, "document_details") ??
      getRecord(rawOcr, "explicit_data");
    const validityIntelligence = getRecord(rawOcr, "validity_intelligence");
    const docDates = getRecord(docData, "dates");
    const expirationYear =
      getText(validityIntelligence, "expiration_year") ||
      getText(docData, "expiration_year");

    return {
      nombre:
        getText(ocr, "nombre", "first_name", "given_names") ||
        getText(name, "name", "first_name"),
      segundoNombre: "",
      apellidoPaterno:
        getText(ocr, "apellido_paterno", "paternal_surname", "first_surname") ||
        getText(name, "paternal_last_name"),
      apellidoMaterno:
        getText(
          ocr,
          "apellido_materno",
          "maternal_surname",
          "second_surname",
        ) || getText(name, "maternal_last_name"),
      direccion: normalizeIdentityOcrAddress(rawOcr),
      curp:
        getText(ocr, "curp") ||
        getText(getRecord(rawOcr, "document_details"), "curp"),
      rfc: getText(ocr, "rfc"),
      claveElector:
        getText(ocr, "clave_elector") || getText(docData, "elector_key"),
      fechaNacimiento: getText(ocr, "fecha_nacimiento", "date_of_birth"),
      seccionElectoral:
        getText(ocr, "seccion_electoral") ||
        getText(docData, "electoral_section", "section"),
      vigencia:
        getText(ocr, "vigencia") ||
        getText(docDates, "expiration_date") ||
        (expirationYear ? `${expirationYear}-12-31` : ""),
      genero: getText(ocr, "genero", "gender", "gender_standardized"),
      email: "",
      phone_number: "",
      dial_code: DEFAULT_NIP_DESTINATION_DIAL_CODE,
    };
  }

  return EMPTY_INE_DATA;
}

/**
 * Pull address from the Proof of Address requirement's verification_data.
 *
 * The backend stores the raw OCR response directly (keys: address, personal_information, etc.)
 * rather than wrapping it in { extracted_data } like the ID endpoint does.
 * We handle both formats for robustness.
 */
function extractAddressFromPOA(
  verificationData: unknown,
): ExtractedAddress | null {
  const verification = isNonEmptyRecord(verificationData)
    ? verificationData
    : null;
  if (!verification) return null;

  // Format 1: direct response shape from verify-proof-of-address.
  if (isNonEmptyRecord(verification.extracted_address)) {
    return normalizeStructuredAddress(verification.extracted_address);
  }

  // Format 2: extracted_data wrapper (same shape as ID verification)
  const extracted = verification.extracted_data;
  if (isNonEmptyRecord(extracted)) {
    const addr = isNonEmptyRecord(extracted.direccion)
      ? extracted.direccion
      : extracted;
    return normalizeStructuredAddress(addr);
  }

  // Legacy fallback: old records persisted before extracted_data was stored
  // alongside the raw OCR response. Formats 3-6 parse raw provider shapes.

  // Format 3: raw OCR response (address at top level or under personal_information)
  const rawAddr =
    getRecord(verification, "address") ??
    getRecord(getRecord(verification, "personal_information"), "address");
  if (rawAddr) {
    const { calle, numeroExterior } = splitStreetNumber(
      getText(rawAddr, "address_line_1"),
    );

    return {
      calle,
      numeroExterior,
      numeroInterior: getText(rawAddr, "address_line_2"),
      colonia: getText(rawAddr, "neighborhood"),
      codigoPostal: getText(rawAddr, "zip"),
      municipio: getText(rawAddr, "municipality"),
      estado: getText(rawAddr, "state"),
    };
  }

  // Format 4: OpenRouter bank statement response
  const holderAddress = getText(
    getRecord(verification, "holder_info"),
    "address",
  );
  if (holderAddress.trim()) {
    const { calle, numeroExterior } = splitStreetNumber(holderAddress);

    return {
      calle,
      numeroExterior,
      numeroInterior: "",
      colonia: "",
      codigoPostal: "",
      municipio: "",
      estado: "",
    };
  }

  // Format 5: predial raw OCR response
  const predialAddr = getRecord(
    getRecord(verification, "owner_information"),
    "notification_address",
  );
  if (predialAddr) {
    return {
      calle: getText(predialAddr, "street"),
      numeroExterior: getText(predialAddr, "number"),
      numeroInterior: "",
      colonia: getText(predialAddr, "neighborhood"),
      codigoPostal: getText(predialAddr, "zip_code"),
      municipio: getText(predialAddr, "municipality"),
      estado: getText(predialAddr, "state"),
    };
  }

  // Format 6: cross-validated from identity (address_from_identity key)
  const crossAddr = getRecord(verification, "address_from_identity");
  if (crossAddr) {
    const calle = getText(crossAddr, "address_line_1", "street");

    return {
      calle,
      numeroExterior: getText(crossAddr, "exterior_number"),
      numeroInterior:
        getText(crossAddr, "interior_number") ||
        getText(crossAddr, "address_line_2"),
      colonia: getText(crossAddr, "neighborhood", "colony"),
      codigoPostal: getText(crossAddr, "zip", "zip_code"),
      municipio: getText(crossAddr, "municipality", "city"),
      estado: getText(crossAddr, "state"),
    };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type ViewMode = "confirmation" | "edit";

interface DataValidationProps {
  onCanAdvanceChange?: (canAdvance: boolean) => void;
}

const POA_POLL_TIMEOUT_MS = 5 * 60 * 1000;

export function DataValidation({ onCanAdvanceChange }: DataValidationProps) {
  // Read state from Redux
  const reduxActiveVault = useAppSelector((state) => state.vault.activeVault);
  const activeTab = useAppSelector((state) => state.vault.activeTab);
  const jwtToken = useAppSelector((state) => state.vault.jwtToken);
  const [viewMode, setViewMode] = useState<ViewMode>("confirmation");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [serverFieldErrors, setServerFieldErrors] = useState<
    Record<string, string>
  >({});
  const [poaPollingTimedOut, setPoaPollingTimedOut] = useState(false);
  const [poaPollingAttempt, setPoaPollingAttempt] = useState(0);
  const [nipDestinationDialCode, setNipDestinationDialCode] =
    useState<NipDestinationDialCode>(DEFAULT_NIP_DESTINATION_DIAL_CODE);
  const [reviewedNipDestinationLocally, setReviewedNipDestinationLocally] =
    useState(false);
  const [locallySavedData, setLocallySavedData] =
    useState<ExtractedINEData | null>(null);
  const [hasUnconfirmedEdits, setHasUnconfirmedEdits] = useState(false);
  const bdcSnapshotRefreshRef = useRef<string | null>(null);
  const { isPending: isSubmitting, run: runSingleFlight } = useSingleFlight();

  const getPoaRefetchInterval = useCallback(
    (folder: Folder | undefined) => {
      const currentFolder = folder ?? reduxActiveVault;
      const currentSubfolder = currentFolder?.subfolders?.find(
        (subfolder) => subfolder.id === activeTab,
      );
      const requirement = currentSubfolder?.requirements?.find(
        isProofOfAddressRequirement,
      );
      const isProcessing =
        requirement?.status === "pending_review" &&
        requirement.workflow_step === "validating" &&
        !isNonEmptyRecord(requirement.verification_data);

      return isProcessing && !poaPollingTimedOut ? 2_000 : false;
    },
    [activeTab, poaPollingTimedOut, reduxActiveVault],
  );

  const { data: queriedVault, refetch: refetchVault } = useVaultFolder(
    reduxActiveVault?.id,
    jwtToken,
    { getRefetchInterval: getPoaRefetchInterval },
  );
  const activeVault = queriedVault ?? reduxActiveVault;

  // Initialize mutation hook
  const mutation = useUpdateWorkflowStep(activeVault?.id);

  // Find current subfolder's requirements
  const currentSubfolder = useMemo(() => {
    if (!activeVault?.subfolders || !activeTab) return null;
    // activeTab is the subfolder.id (not slug) so the correct subfolder is selected
    // even when several share a role (one per participant).
    return activeVault.subfolders.find((sf) => sf.id === activeTab) ?? null;
  }, [activeVault, activeTab]);

  const requirements = useMemo(
    () => currentSubfolder?.requirements ?? [],
    [currentSubfolder],
  );

  // Find ID requirement
  const idRequirement = useMemo(
    () => requirements.find(isIdentityRequirement),
    [requirements],
  );

  // Find Proof of Address requirement
  const poaRequirement = useMemo(
    () => requirements.find(isProofOfAddressRequirement),
    [requirements],
  );

  const isAwaitingPoaOcr =
    poaRequirement?.status === "pending_review" &&
    poaRequirement.workflow_step === "validating" &&
    !isNonEmptyRecord(poaRequirement.verification_data);

  useEffect(() => {
    if (!isAwaitingPoaOcr || poaPollingTimedOut) return;
    const timeoutId = window.setTimeout(() => {
      setPoaPollingTimedOut(true);
    }, POA_POLL_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isAwaitingPoaOcr, poaPollingAttempt, poaPollingTimedOut]);

  const bdcRequirement = useMemo(
    () =>
      requirements.find((requirement) =>
        isBdcAuthorizationRequirement(requirement.slug, requirement.type),
      ),
    [requirements],
  );

  const isParticipantMismatch = isBdcActorMismatch(
    jwtToken,
    currentSubfolder?.slug,
  );
  const supportsNativeBdc = isNativeBdcSupported(
    bdcRequirement,
    currentSubfolder,
  );

  const bdcStatusQuery = useBdcStatus(bdcRequirement?.id, {
    enabled:
      supportsNativeBdc && Boolean(bdcRequirement) && !isParticipantMismatch,
  });
  const requiresBdcDestination = Boolean(
    supportsNativeBdc &&
      bdcRequirement &&
      jwtToken &&
      !isParticipantMismatch &&
      bdcStatusQuery.data?.sic_source !== "circulo_de_credito",
  );
  const persistedBdc = bdcRequirement?.verification_data?.bdc;
  const effectiveBdcSicSource = getBdcSicSource(
    bdcStatusQuery.data,
    bdcRequirement,
  );
  useEffect(() => {
    if (
      !requiresBdcDestination ||
      bdcStatusQuery.isFetching ||
      bdcStatusQuery.isError
    )
      return;
    const needsRefresh =
      bdcStatusQuery.data?.status === "capturing_inputs" &&
      bdcStatusQuery.data.requirement_status === Status.PendingUpload &&
      (bdcRequirement?.status !== Status.PendingUpload ||
        persistedBdc?.inputs_consumed_at ||
        (persistedBdc?.last_status &&
          persistedBdc.last_status !== "capturing_inputs"));
    if (!needsRefresh) {
      bdcSnapshotRefreshRef.current = null;
      return;
    }
    const snapshot = JSON.stringify([
      bdcRequirement?.id,
      bdcRequirement?.status,
      persistedBdc?.inputs_consumed_at,
      persistedBdc?.last_status,
    ]);
    if (bdcSnapshotRefreshRef.current === snapshot) return;
    bdcSnapshotRefreshRef.current = snapshot;
    void refetchVault();
  }, [
    bdcRequirement?.id,
    bdcRequirement?.status,
    persistedBdc?.inputs_consumed_at,
    persistedBdc?.last_status,
    bdcStatusQuery.data?.status,
    bdcStatusQuery.data?.requirement_status,
    bdcStatusQuery.isFetching,
    bdcStatusQuery.isError,
    refetchVault,
    requiresBdcDestination,
  ]);
  const canRecoverLegacyCapture =
    isLegacyBdcCaptureRecoveryEligible(bdcRequirement, bdcStatusQuery.data) &&
    !persistedBdc?.credit_report_id;
  const canCorrectBdcIdentity = Boolean(
    requiresBdcDestination &&
      (bdcRequirement?.status === Status.PendingUpload ||
        canRecoverLegacyCapture) &&
      bdcStatusQuery.data?.status === "capturing_inputs" &&
      bdcStatusQuery.data.is_active !== false &&
      !bdcStatusQuery.data.start_recovery_pending &&
      !persistedBdc?.inputs_consumed_at &&
      !persistedBdc?.start_recovery_pending &&
      (canRecoverLegacyCapture ||
        !persistedBdc?.last_status ||
        persistedBdc.last_status === "capturing_inputs") &&
      !hasValidatedPersonForm(requirements),
  );
  const strictBdcValidation = Boolean(
    canCorrectBdcIdentity && effectiveBdcSicSource === "buro_de_credito",
  );
  const canConfirmApprovedIdentity = Boolean(
    requiresBdcDestination &&
      hasValidatedPersonForm(requirements) &&
      canCaptureBdcIdentity(bdcRequirement, bdcStatusQuery.data),
  );
  const approvedPersonData = useMemo(() => {
    if (!canConfirmApprovedIdentity) return null;
    const formSlugs = currentSubfolder?.slug.endsWith("-pm")
      ? ["legal-rep-form", "legal-rep-short-form"]
      : currentSubfolder?.slug.includes("guarantor")
        ? ["guarantor-form", "guarantor-cja-form"]
        : ["tenant-form", "tenant-cja-form"];
    const forms = requirements.filter(
      (requirement) =>
        formSlugs.includes(requirement.slug) &&
        requirement.status === Status.Valid,
    );
    return forms.length === 1 ? extractApprovedPersonForm(forms[0]) : null;
  }, [canConfirmApprovedIdentity, currentSubfolder?.slug, requirements]);
  const requireBirthDate = Boolean(
    (canCorrectBdcIdentity || canConfirmApprovedIdentity) &&
      isBdcNaturalPersonSubfolder(currentSubfolder?.slug),
  );
  const bdcStatusError = useMemo(
    () =>
      bdcStatusQuery.isError ? mapBdcAxiosError(bdcStatusQuery.error) : null,
    [bdcStatusQuery.error, bdcStatusQuery.isError],
  );
  const isBdcDestinationLoading =
    requiresBdcDestination && !bdcStatusQuery.data && bdcStatusQuery.isLoading;
  const isBdcDestinationMissing =
    requiresBdcDestination &&
    !bdcStatusQuery.data &&
    bdcStatusQuery.isError &&
    bdcStatusError !== null;
  const canRetryBdcStatus = bdcStatusError
    ? !isDeterministicBdcError(bdcStatusError.code)
    : false;
  const actorContact = useMemo(
    () =>
      supportsNativeBdc
        ? getActorContactData(activeVault?.metadata, currentSubfolder?.slug)
        : getParticipantContactData(
            currentSubfolder,
            idRequirement?.verification_data,
          ),
    [
      activeVault?.metadata,
      currentSubfolder,
      idRequirement?.verification_data,
      supportsNativeBdc,
    ],
  );
  // The invitation metadata is the participant credential and the only raw
  // email the actor needs. BDC status exposes only a masked destination plus
  // the server-side match flag.
  const isDelegatedContact =
    requiresBdcDestination &&
    (decodeVaultJwtActor(jwtToken) === "advisor" ||
      (decodeVaultJwtActor(jwtToken) === "tenant" &&
        currentSubfolder?.slug.includes("guarantor")));
  const hasBdcEmailMismatch =
    requiresBdcDestination &&
    bdcStatusQuery.data?.email_matches_metadata === false;
  const persistedContact = useMemo(
    () => getVerificationContactData(idRequirement?.verification_data),
    [idRequirement?.verification_data],
  );

  const hasStoredConfirmedData =
    !!idRequirement?.verification_data?.confirmed_data;
  const hasValidStoredConfirmedData = useMemo(
    () =>
      hasValidIDData(
        locallySavedData ?? idRequirement?.verification_data?.confirmed_data,
        {
          requireDialCode: false,
          requireStrictCurp: requiresBdcDestination,
          requireExteriorNumber: requiresBdcDestination,
          requireBirthDate,
          strictBdcValidation,
        },
      ),
    [
      idRequirement?.verification_data?.confirmed_data,
      locallySavedData,
      requiresBdcDestination,
      requireBirthDate,
      strictBdcValidation,
    ],
  );
  const hasCompletedNipDestinationReview =
    !requiresBdcDestination ||
    reviewedNipDestinationLocally ||
    hasReviewedNipDestination(idRequirement?.verification_data?.confirmed_data);
  const requiresAddressReconfirmation =
    requiresBdcDestination &&
    idRequirement?.workflow_step === "data_confirmation";

  useEffect(() => {
    setReviewedNipDestinationLocally(false);
    setLocallySavedData(null);
    setHasUnconfirmedEdits(false);
    setServerFieldErrors({});
  }, [activeVault?.id, activeTab, bdcRequirement?.id, idRequirement?.id]);

  useEffect(() => {
    if (requiresAddressReconfirmation) {
      setLocallySavedData(null);
      setReviewedNipDestinationLocally(false);
      setViewMode("confirmation");
    }
  }, [requiresAddressReconfirmation]);

  useEffect(() => {
    const nextDialCode =
      (isDelegatedContact ? bdcStatusQuery.data?.dial_code : undefined) ??
      persistedContact?.dial_code ??
      actorContact?.dial_code ??
      (requiresBdcDestination ? bdcStatusQuery.data?.dial_code : undefined);
    if (isValidNipDestinationDialCode(nextDialCode)) {
      setNipDestinationDialCode(nextDialCode);
    }
  }, [
    actorContact?.dial_code,
    bdcStatusQuery.data?.dial_code,
    persistedContact?.dial_code,
    isDelegatedContact,
    requiresBdcDestination,
  ]);

  // Build combined data: personal info from ID + address from POA when available.
  const combinedData: ExtractedINEData | null = useMemo(() => {
    if (locallySavedData) {
      const data = applyContactDefaults(
        locallySavedData,
        actorContact,
        nipDestinationDialCode,
      );
      return data;
    }

    if (!idRequirement) return null;

    const storedPersonal = extractPersonalDataFromID(
      idRequirement.verification_data,
    );
    const personal = approvedPersonData
      ? fillEmptyTextFields(storedPersonal, approvedPersonData)
      : storedPersonal;

    // New POA evidence requires confirming its address again. Otherwise retain
    // manual corrections, including edits saved before final confirmation.
    const confirmedAddress =
      hasStoredConfirmedData && !requiresAddressReconfirmation
        ? personal.direccion
        : null;
    const poaAddress = extractAddressFromPOA(poaRequirement?.verification_data);
    let address: ExtractedAddress =
      confirmedAddress ?? poaAddress ?? personal.direccion;
    if (approvedPersonData) {
      address = fillEmptyTextFields(
        confirmedAddress ?? EMPTY_ADDRESS,
        approvedPersonData.direccion,
      );
      if (poaAddress) address = fillEmptyTextFields(address, poaAddress);
    }

    const data = applyContactDefaults(
      {
        ...personal,
        direccion: address,
      },
      actorContact,
      nipDestinationDialCode,
    );
    return data;
  }, [
    actorContact,
    hasStoredConfirmedData,
    idRequirement,
    locallySavedData,
    approvedPersonData,
    nipDestinationDialCode,
    poaRequirement,
    requiresAddressReconfirmation,
  ]);

  const validationErrors = useMemo(() => {
    if (!combinedData) return {};
    return validateIDData(combinedData, {
      requireDialCode: requiresBdcDestination,
      requireStrictCurp: requiresBdcDestination,
      requireExteriorNumber: requiresBdcDestination,
      requireBirthDate,
      strictBdcValidation,
    });
  }, [
    strictBdcValidation,
    combinedData,
    requiresBdcDestination,
    requireBirthDate,
  ]);

  // Check if all required fields are filled (personal + address).
  // When any required field is missing, force the user into edit mode.
  const hasRequiredPersonalFields = useMemo(() => {
    if (!combinedData) return false;
    return !(
      validationErrors.nombre ||
      validationErrors.apellidoPaterno ||
      validationErrors.apellidoMaterno ||
      validationErrors.curp ||
      validationErrors.genero ||
      validationErrors.fechaNacimiento ||
      validationErrors.rfc
    );
  }, [combinedData, validationErrors]);

  const hasRequiredAddressFields = useMemo(() => {
    if (!combinedData) return false;
    return !(
      validationErrors["direccion.calle"] ||
      validationErrors["direccion.numeroExterior"] ||
      validationErrors["direccion.numeroInterior"] ||
      validationErrors["direccion.colonia"] ||
      validationErrors["direccion.codigoPostal"] ||
      validationErrors["direccion.municipio"] ||
      validationErrors["direccion.estado"]
    );
  }, [combinedData, validationErrors]);

  const hasMissingRequiredFields = combinedData
    ? Object.keys(validationErrors).length > 0
    : false;
  const hasSubmittedWorkflowStep = isDataValidationSubmittedWorkflowStep(
    idRequirement?.workflow_step,
  );
  const isAlreadyConfirmed =
    !hasUnconfirmedEdits &&
    !isAwaitingPoaOcr &&
    !poaPollingTimedOut &&
    !hasBdcEmailMismatch &&
    poaRequirement?.status !== "invalid" &&
    hasStoredConfirmedData &&
    (!requiresBdcDestination ||
      (hasValidStoredConfirmedData &&
        hasSubmittedWorkflowStep &&
        hasCompletedNipDestinationReview));

  useEffect(() => {
    onCanAdvanceChange?.(isAlreadyConfirmed && viewMode !== "edit");
    return () => onCanAdvanceChange?.(false);
  }, [isAlreadyConfirmed, onCanAdvanceChange, viewMode]);

  useEffect(() => {
    if (
      combinedData &&
      hasMissingRequiredFields &&
      !isAlreadyConfirmed &&
      !isAwaitingPoaOcr
    ) {
      verificationDebugLog(
        "dataValidation.missingRequiredFields",
        {
          idRequirementId: idRequirement?.id,
          poaRequirementId: poaRequirement?.id,
          hasRequiredPersonalFields,
          hasRequiredAddressFields,
        },
        withoutBdcContactForDebug(combinedData),
      );
      setViewMode("edit");
    }
  }, [
    combinedData,
    hasMissingRequiredFields,
    hasRequiredAddressFields,
    hasRequiredPersonalFields,
    idRequirement?.id,
    isAlreadyConfirmed,
    isAwaitingPoaOcr,
    poaRequirement?.id,
  ]);

  useEffect(() => {
    if (!combinedData) return;

    verificationDebugLog(
      "dataValidation.combinedData",
      {
        idRequirementId: idRequirement?.id,
        poaRequirementId: poaRequirement?.id,
        isAlreadyConfirmed,
        hasMissingRequiredFields,
        sourceSummaries: {
          identity: summarizeCombinedVerificationData(
            idRequirement?.verification_data,
          ),
          proofOfAddress: summarizeCombinedVerificationData(
            poaRequirement?.verification_data,
          ),
        },
      },
      withoutBdcContactForDebug(combinedData),
    );
  }, [
    combinedData,
    hasMissingRequiredFields,
    idRequirement?.id,
    idRequirement?.verification_data,
    isAlreadyConfirmed,
    poaRequirement?.id,
    poaRequirement?.verification_data,
  ]);

  // Handlers
  const persistConfirmedData = useCallback(
    async (
      confirmedData: ExtractedINEData,
      workflowStep: DataValidationWorkflowStep,
      options: ConfirmedDataPayloadOptions = {},
    ): Promise<boolean> => {
      if (!idRequirement || !activeVault) return false;

      setSubmitError(null);

      try {
        await mutation.mutateAsync({
          reqId: idRequirement.id,
          workflow_step: workflowStep,
          confirmed_data: buildConfirmedDataPayload(confirmedData, options),
        });
        setServerFieldErrors({});
        verificationDebugLog(
          "dataValidation.confirmedData.saved",
          {
            idRequirementId: idRequirement.id,
            activeVaultId: activeVault.id,
          },
          withoutBdcContactForDebug(confirmedData),
        );
        return true;
      } catch (error: unknown) {
        const fieldErrors = getWorkflowFieldErrors(error);
        if (canCorrectBdcIdentity && Object.keys(fieldErrors).length > 0) {
          setServerFieldErrors(fieldErrors);
          setViewMode("edit");
          return false;
        }
        const message =
          error instanceof Error
            ? error.message
            : "Ocurrio un error al guardar los datos. Intenta de nuevo.";
        verificationDebugLog(
          "dataValidation.confirmedData.error",
          {
            idRequirementId: idRequirement.id,
            activeVaultId: activeVault.id,
            error: message,
          },
          withoutBdcContactForDebug(confirmedData),
        );
        setSubmitError(message);
        return false;
      }
    },
    [activeVault, canCorrectBdcIdentity, idRequirement, mutation],
  );

  const handleConfirm = useCallback(async () => {
    if (!combinedData || hasMissingRequiredFields || hasBdcEmailMismatch) {
      return;
    }
    const confirmedData = normalizeConfirmedDataForSave(combinedData);
    const saved = await runSingleFlight(() =>
      persistConfirmedData(confirmedData, "completed", {
        includeContact: requiresBdcDestination,
        markNipDestinationReviewed: requiresBdcDestination,
        nipDestinationDialCode: confirmedData.dial_code,
      }),
    );
    if (saved) {
      setHasUnconfirmedEdits(false);
      setLocallySavedData(confirmedData);
      if (requiresBdcDestination) {
        setReviewedNipDestinationLocally(true);
      }
    }
  }, [
    combinedData,
    hasBdcEmailMismatch,
    hasMissingRequiredFields,
    persistConfirmedData,
    requiresBdcDestination,
    runSingleFlight,
  ]);

  const handleEdit = useCallback(() => {
    setViewMode("edit");
  }, []);

  const handleEditSave = useCallback(
    async (editedData: ExtractedINEData) => {
      const confirmedData = normalizeConfirmedDataForSave(editedData);
      const saved = await runSingleFlight(() =>
        persistConfirmedData(
          confirmedData,
          requiresBdcDestination ? "data_edit" : "completed",
          { includeContact: requiresBdcDestination },
        ),
      );
      if (saved) {
        setServerFieldErrors({});
        setHasUnconfirmedEdits(requiresBdcDestination);
        setNipDestinationDialCode(confirmedData.dial_code);
        setLocallySavedData(confirmedData);
        setViewMode("confirmation");
      }
    },
    [persistConfirmedData, requiresBdcDestination, runSingleFlight],
  );

  const handleEditCancel = useCallback(() => {
    setServerFieldErrors({});
    setViewMode("confirmation");
  }, []);

  // -----------------------------------------------------------------------
  // Render: the POA worker has not produced OCR data yet
  // -----------------------------------------------------------------------
  if (isAwaitingPoaOcr && !poaPollingTimedOut) {
    const documentType = poaRequirement.form_values?.find(
      (field) => field.key === "document_type",
    )?.value;
    return (
      <ProofOfAddressProcessingLoader
        documentType={
          typeof documentType === "string" ? documentType : undefined
        }
      />
    );
  }

  // Rejections belong to the Proof of Address step, which owns the feedback
  // banner and re-upload form. VaultPageRedesign derives that step as active;
  // avoid ever presenting a rejection as a technical validation failure here.
  if (poaRequirement?.status === "invalid") {
    return null;
  }

  if (poaPollingTimedOut) {
    return (
      <div className="py-16 px-4 sm:px-6 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">
          No pudimos validar el comprobante
        </h3>
        <p className="text-sm text-slate-500 max-w-md mb-4">
          Regresa al paso anterior y vuelve a subir el comprobante. No es
          posible confirmar datos vacíos o continuar mientras siga pendiente.
        </p>
        {poaPollingTimedOut && (
          <button
            className="text-sm text-[#28397a] hover:underline font-medium"
            onClick={() => {
              setPoaPollingTimedOut(false);
              setPoaPollingAttempt((attempt) => attempt + 1);
              void refetchVault();
            }}
          >
            Consultar estado de nuevo
          </button>
        )}
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Already confirmed state
  // -----------------------------------------------------------------------
  if (isAlreadyConfirmed && viewMode !== "edit") {
    return (
      <div className="py-16 px-4 sm:px-6 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" strokeWidth={1.5} />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">
          Informacion enviada
        </h3>
        <p className="text-sm text-slate-500 max-w-md">
          Tus datos fueron enviados correctamente. Puedes continuar con el
          siguiente paso.
        </p>
        {canCorrectBdcIdentity && (
          <button
            type="button"
            className="mt-4 text-sm text-[#28397a] hover:underline font-medium"
            onClick={handleEdit}
          >
            Corregir datos
          </button>
        )}
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Safety fallback: prerequisites missing (should not happen due to locking)
  // -----------------------------------------------------------------------
  if (!idRequirement) {
    return null;
  }

  if (isBdcDestinationLoading) {
    return (
      <div className="py-16 px-4 sm:px-6 flex flex-col items-center text-center">
        <Loader2 className="w-10 h-10 text-[#28397a] animate-spin mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Confirmando destino del NIP...
        </h3>
        <p className="text-sm text-slate-500">Esto tomara solo un momento.</p>
      </div>
    );
  }

  if (isBdcDestinationMissing && bdcStatusError) {
    return (
      <div className="py-16 px-4 sm:px-6 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-red-500" strokeWidth={1.5} />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">
          No pudimos confirmar el destino del NIP
        </h3>
        <p className="text-sm text-red-500 max-w-md mb-4">
          {bdcStatusError.message}
        </p>
        {canRetryBdcStatus && (
          <button
            className="text-sm text-[#28397a] hover:underline font-medium disabled:cursor-not-allowed disabled:text-slate-400"
            disabled={bdcStatusQuery.isFetching}
            onClick={() => {
              void bdcStatusQuery.refetch();
            }}
          >
            {bdcStatusQuery.isFetching ? "Reintentando..." : "Reintentar"}
          </button>
        )}
      </div>
    );
  }

  if (hasBdcEmailMismatch) {
    return (
      <div className="py-16 px-4 sm:px-6 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-amber-600" strokeWidth={1.5} />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">
          El correo del expediente no coincide
        </h3>
        <p className="text-sm text-slate-600 max-w-md">
          Por seguridad no podemos cambiar el destino del NIP desde este flujo.
          Contacta al equipo de soporte para corregir el correo de la
          investigación.
        </p>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Loading overlay when submitting
  // -----------------------------------------------------------------------
  if (isSubmitting) {
    return (
      <div className="py-16 px-4 sm:px-6 flex flex-col items-center text-center">
        <Loader2 className="w-10 h-10 text-[#28397a] animate-spin mb-4" />
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Guardando datos...
        </h3>
        <p className="text-sm text-slate-500">Esto tomara solo un momento.</p>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Error state
  // -----------------------------------------------------------------------
  if (submitError) {
    return (
      <div className="py-16 px-4 sm:px-6 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <AlertCircle className="w-8 h-8 text-red-500" strokeWidth={1.5} />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">
          Error al guardar
        </h3>
        <p className="text-sm text-red-500 max-w-md mb-4">{submitError}</p>
        <button
          className="text-sm text-[#28397a] hover:underline font-medium"
          onClick={() => setSubmitError(null)}
        >
          Intentar de nuevo
        </button>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render: Confirmation or Edit form
  // -----------------------------------------------------------------------
  const formData = combinedData ?? EMPTY_INE_DATA;
  if (viewMode === "edit") {
    return (
      <IDDataEditForm
        data={formData}
        onSave={handleEditSave}
        onCancel={handleEditCancel}
        allowCancel={!hasMissingRequiredFields}
        isSubmitting={isSubmitting}
        requiresBdcAuthorization={requiresBdcDestination}
        strictBdcValidation={strictBdcValidation}
        serverFieldErrors={serverFieldErrors}
        requireBirthDate={requireBirthDate}
        contactReadOnly={isDelegatedContact}
      />
    );
  }

  return (
    <IDDataConfirmationForm
      data={formData}
      onConfirm={handleConfirm}
      onEdit={handleEdit}
      isSubmitting={isSubmitting}
      requiresBdcAuthorization={requiresBdcDestination}
    />
  );
}
